import React, { useContext, useEffect, useMemo, useState } from "react";
import { GridGenerator } from "@actinc/dls/components";
import { AlertContext } from "@actinc/dls/context";
import _ from "lodash/fp";
import { Box, Button, Paper, Typography } from "@mui/material";
import { useForm, useFormState } from "react-hook-form";
import { useClient } from "urql";

import {
  ControlledFormInput,
  ControlledFormSelect,
  DataGrid,
} from "../../../components";

import { getProgramOptions, setTitle } from "../../../services/utils";
import { ProgramsContext } from "../../../programsProvider";
import { useCallback } from "react";
import {
  getAlertMessage,
  getItemsWithoutDBData,
  getItemsWithoutPermission,
  getItemsWithPermission,
  getItemsWithPermissionDifferentProgram,
  getItemsWithPermissionOfProgram,
  getSelectedItemFromDB,
  getSelectedItemNames,
  getSelectedProgram,
  updateItemsInDB,
} from "./BatchUpdateContentUtils";

const CHUNKSIZE = 25;

const BatchUpdateContent = () => {
  const client = useClient();
  const datagridKey = "buc";
  const alertContext = useContext(AlertContext);
  const allPrograms = useContext(ProgramsContext);

  const { control, getValues, handleSubmit, reset, setValue, watch } = useForm({
    defaultValues,
  });
  const { isSubmitting } = useFormState({ control });
  const [loading, setLoading] = useState(false);
  const [bucResults, setBucResults] = useState(false);

  const userAccessiblePrograms = useMemo(
    () => getProgramOptions(allPrograms)("batchUpdateContent"),
    [allPrograms],
  );

  const selectedProgram = watch("program");

  const handleResets = useCallback(() => {
    reset({ ...defaultValues });
  }, [reset]);

  useEffect(() => {
    handleResets();
  }, [handleResets]);
  useEffect(() => {
    setTitle(["CDE", "Batch Update Content"]);
  }, []);

  const submitBUC = async () => {
    setLoading(true);

    const program = getSelectedProgram(selectedProgram)(userAccessiblePrograms);
    const itemNames = getSelectedItemNames(getValues);

    if (itemNames.length && program) {
      setBucResults([]);
      const selectedItemsFromDB =
        await getSelectedItemFromDB(CHUNKSIZE)(client)(itemNames);

      const itemsWithoutDBData =
        getItemsWithoutDBData(itemNames)(selectedItemsFromDB);

      const userProgramsWithPermission = new Set(
        userAccessiblePrograms.map((program) => program.value),
      );

      const itemsWithPermission = getItemsWithPermission(selectedItemsFromDB)(
        userProgramsWithPermission,
      );

      const itemsWithoutPermission = getItemsWithoutPermission(
        selectedItemsFromDB,
      )(userProgramsWithPermission).concat(itemsWithoutDBData);

      const itemWithPermissionOfSelectedProgram =
        getItemsWithPermissionOfProgram(itemsWithPermission)(program);

      const itemsWithPermissionOfDifferentProgram =
        getItemsWithPermissionDifferentProgram(itemsWithPermission)(program);

      const itemsUpdatedFromDB = await updateItemsInDB(CHUNKSIZE)(client)(
        itemWithPermissionOfSelectedProgram,
      )(program);

      const resultsMapped = itemsUpdatedFromDB
        .concat(itemsWithoutPermission)
        .concat(itemsWithPermissionOfDifferentProgram);

      const alertMessage = getAlertMessage(itemsWithoutPermission);
      alertContext.actions.addAlert(alertMessage);

      setBucResults(_.uniqWith(_.isEqual)(resultsMapped));
    }

    setLoading(false);
  };

  const columns = [
    {
      field: "name",
      headerName: "Item/Passage Name",
      sortComparator: (v1, v2) =>
        v1.localeCompare(v2, "en", { sensitivity: "base" }),
      type: "string",
      width: 400,
    },
    {
      field: "type",
      headerName: "Type",
      sortComparator: (v1, v2) =>
        v1.localeCompare(v2, "en", { sensitivity: "base" }),
      type: "string",
      width: 200,
      valueGetter: upperCase("type"),
    },
    {
      field: "statusMessage",
      headerName: "Message",
      sortComparator: (v1, v2) =>
        v1.localeCompare(v2, "en", { sensitivity: "base" }),
      type: "string",
      width: 200,
    },
  ];

  const dataGridProps = {
    autoHeight: true,
    columns,
    datagridKey,
    loading,
    rows: bucResults,
    sortModel: [{ field: "name", sort: "asc" }],
  };

  return (
    <>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          margin: 2,
        }}
      >
        <Typography variant="h4">Batch Update Content</Typography>
      </Box>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          width: "100%",
        }}
      >
        <Paper
          elevation={1}
          sx={{
            display: "flex",
            flexDirection: "column",
            margin: 2,
            padding: 1,
          }}
        >
          <Box sx={{ flexGrow: 1 }}>
            <form onSubmit={handleSubmit(submitBUC)}>
              <Box
                sx={{
                  alignItems: "end",
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                <GridGenerator
                  groups={[
                    [
                      {
                        children: (
                          <ControlledFormSelect
                            control={control}
                            fullWidth={true}
                            id={`buc-program`}
                            label="Program"
                            name={`program`}
                            options={userAccessiblePrograms?.map((value) => ({
                              label: value?.label,
                              value: value?.label,
                            }))}
                            rules={{
                              required: true,
                            }}
                          />
                        ),
                        key: "buc-program",
                      },
                    ],
                    [
                      {
                        children: (
                          <ControlledFormInput
                            control={control}
                            fullWidth={true}
                            inputProps={{
                              maxRows: 10,
                              minRows: 10,
                            }}
                            label="Item Names"
                            multiline={true}
                            name="itemNames"
                          />
                        ),
                        key: `item-names`,
                      },
                    ],
                  ]}
                />
                <Box sx={{ display: "flex", flexDirection: "row" }}>
                  <Button
                    onClick={() => {
                      reset({ ...defaultValues });
                      setBucResults(null);
                      setValue("itemNames", "");
                    }}
                    sx={{ marginRight: 2 }}
                    variant="outlined"
                  >
                    Reset
                  </Button>
                  <Button
                    disabled={isSubmitting}
                    sx={{ marginRight: 2 }}
                    type="submit"
                    variant="contained"
                  >
                    Batch Update Content
                  </Button>
                </Box>
              </Box>
            </form>
          </Box>
        </Paper>
      </Box>
      {(bucResults || loading) && (
        <Paper
          elevation={1}
          sx={{
            display: "flex",
            flexDirection: "column",
            flexGrow: 1,
            margin: 1,
          }}
        >
          <Box
            sx={{
              flexGrow: 1,
              minHeight: "500px",
            }}
          >
            <DataGrid {...dataGridProps}></DataGrid>
          </Box>
        </Paper>
      )}
    </>
  );
};

const defaultValues = {
  itemNames: null,
  program: "",
};

const upperCase = (key) => (params) => params.row[key].toUpperCase();

export { BatchUpdateContent };
